/**
 * The guardrail that informs and never blocks (3.2). Nothing is said until she
 * picks a card up; the alert takes this slot then. It still reserves no space
 * when there is nothing to say, so picking a card up is the only thing that moves
 * the page — 3.2 is clicked through on that.
 *
 * One component for both views. On My week the target is a day; on My day it is a
 * time on the axis, and the strip carries one more thing: what the drop would land
 * across. Until 2026-09-19 a drop onto another stop's span was refused outright
 * and the strip said so in the error tone; Pete took the refusal off — *"I want to
 * allow dropping visits into conflict with other blocks/visits. It makes it hard
 * to rearrange things otherwise"* — so the overlap is now **said and allowed**, at
 * warn, beside the checks rather than in place of them. That was the one place
 * this demo's guardrails blocked instead of informing.
 *
 * A **block** being re-timed has no evaluation at all: there is no patient behind
 * it, so there is no window, frequency or period to read, and the strip says only
 * where it would land and what it would land across. An empty checks row would be
 * three questions answered about nobody.
 */
import { Alert, Stack, Typography } from "@mui/material";
import type { RealMoveEvaluation } from "../../engine/moveGuard";
import { useDemoBranchClock } from "../../components/branchClock";

/**
 * The shell's AppBar is `position: fixed` over the scrolling main area
 * (`components/layout/AuthenticatedRoot.tsx`), and the phantom toolbar that pads
 * that scroller is what the content clears at rest. A sticky child has to clear
 * the same height or it pins underneath the app bar.
 */
export const APP_BAR_HEIGHT_PX = 64;

export function GuardrailStrip({
  patientName,
  targetLabel,
  evaluation,
  overlap,
  stickyTop,
}: {
  /** The patient on a visit; the block's own title when a block is being re-timed. */
  patientName: string;
  /** "Thu Sep 24" on the week; on the day the hour, already phrased on the user's own clock ("12:30PM" / "1230"). */
  targetLabel: string;
  /** Absent for a block: nothing about a patient is being asked. */
  evaluation?: RealMoveEvaluation;
  /**
   * My day only: what the span would land across, already phrased — *"overlaps
   * K. Osei 12:00PM–12:45PM"*. It is information, not a refusal: the drop lands.
   */
  overlap?: string;
  /**
   * How far down its own scroll container the strip pins. My week scrolls under
   * the shell's fixed app bar, so it clears that; My day's panel is its own
   * scroll container inside a dialog with no app bar over it, so it pins at the
   * very top of the body and passes `0`. A shared default of 64 inside a dialog
   * floated the strip a clear app bar's height below the first thing it sits over.
   */
  stickyTop?: number;
}) {
  const top = stickyTop ?? APP_BAR_HEIGHT_PX;
  // An overlap is worth a warning and never an error: nothing is being prevented.
  // A hard evaluation still outranks it.
  const clock = useDemoBranchClock();
  const severity = evaluation?.level === "block" ? "error" : evaluation?.level === "warn" || overlap ? "warning" : "success";
  return (
    // Sticky, because the drop target is usually below the fold: next week is a
    // full row down, and a guardrail she has to scroll away from to reach the day
    // she is aiming at is not informing the drop. `outlined` is transparent, so it
    // takes the paper background to sit over the week it is pinned above.
    <Alert
      severity={severity}
      variant="outlined"
      data-testid="guardrail-strip"
      sx={{ py: 0.25, position: "sticky", top, zIndex: 2, bgcolor: "background.paper" }}
    >
      <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
        <Typography variant="body2">
          {patientName} → {targetLabel}
        </Typography>
        {evaluation && (
          <>
            {/* Each only when there is something to say (Pete, 2026-09-22). A
                census visit has no plan behind it, so two of the three are
                unanswerable and used to draw an em dash apiece. */}
            {evaluation.checks.frequency && <Typography variant="body2">Frequency this week {evaluation.checks.frequency}</Typography>}
            {evaluation.checks.periodRisk && <Typography variant="body2">Visits at risk {evaluation.checks.periodRisk}</Typography>}
            {evaluation.checks.window && <Typography variant="body2">Patient window {clock.sentence(evaluation.checks.window)}</Typography>}
            {evaluation.reasons[0] && <Typography variant="body2">{clock.sentence(evaluation.reasons[0])}</Typography>}
          </>
        )}
        {/* Beside the checks, not instead of them: it is one more thing she knows
            about the drop, and the drop still happens. */}
        {overlap && <Typography variant="body2">{overlap}</Typography>}
      </Stack>
    </Alert>
  );
}
